@layer components {
  .button,
  .iconButton {
    --button-bg: var(--color-surface);
    --button-fg: var(--color-text);
    --button-edge: var(--color-border-strong);
    --button-shade: var(--color-border);

    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-2);
    min-block-size: var(--tap-target);
    padding-inline: var(--space-5);
    border: 1.5px solid var(--button-edge);
    border-radius: var(--radius-full);
    background: var(--button-bg);
    color: var(--button-fg);
    font-size: var(--text-sm);
    font-weight: 620;
    line-height: 1.2;
    white-space: nowrap;
    text-decoration: none;
    box-shadow: inset 0 -2px 0 var(--button-shade);
    transition:
      background-color var(--duration-fast) var(--ease-out),
      border-color var(--duration-fast) var(--ease-out),
      color var(--duration-fast) var(--ease-out),
      box-shadow var(--duration-fast) var(--ease-out),
      scale var(--duration-spring) var(--ease-spring),
      translate var(--duration-spring) var(--ease-spring);
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;

    & svg {
      flex: none;
      inline-size: 1.125rem;
      block-size: 1.125rem;
      stroke-width: 2.1;
    }

    @media (hover: hover) {
      &:hover:not(:disabled) {
        --button-bg: var(--color-surface-hover);
        --button-edge: var(--color-text-faint);
      }
    }

    &:active:not(:disabled) {
      scale: 0.96;
      translate: 0 1px;
      box-shadow: inset 0 0 0 var(--button-shade);
      transition-duration: 60ms;
    }

    &:disabled {
      cursor: default;
      opacity: 0.5;
    }

    &[data-variant="primary"] {
      --button-bg: var(--color-action);
      --button-fg: var(--color-on-action);
      --button-edge: var(--color-action-shade);
      --button-shade: var(--color-action-shade);

      box-shadow:
        inset 0 -3px 0 var(--button-shade),
        var(--shadow-sm);

      @media (hover: hover) {
        &:hover:not(:disabled) {
          --button-bg: var(--color-action-hover);
          --button-edge: var(--color-action-shade);

          scale: 1.03;
        }
      }

      &:active:not(:disabled) {
        scale: 0.96;
        box-shadow: inset 0 0 0 var(--button-shade);
      }
    }

    &[data-variant="frame"] {
      --button-bg: var(--color-frame-pill);
      --button-fg: var(--color-on-frame);
      --button-edge: transparent;
      --button-shade: transparent;

      @media (hover: hover) {
        &:hover:not(:disabled) {
          --button-bg: var(--color-frame-pill-hover);
          --button-edge: transparent;
        }
      }
    }

    &[data-variant="danger"] {
      --button-bg: var(--color-danger);
      --button-fg: var(--color-surface);
      --button-edge: transparent;
      --button-shade: color-mix(in oklch, var(--color-danger), black 28%);

      @media (hover: hover) {
        &:hover:not(:disabled) {
          --button-bg: color-mix(in oklch, var(--color-danger), black 10%);
          --button-edge: transparent;
        }
      }
    }

    &[data-variant="ghost"] {
      --button-bg: transparent;
      --button-fg: var(--color-text-muted);
      --button-edge: transparent;
      --button-shade: transparent;

      @media (hover: hover) {
        &:hover:not(:disabled) {
          --button-bg: var(--color-accent-soft);
          --button-fg: var(--color-accent);
          --button-edge: transparent;
        }
      }
    }

    &[data-variant="outline-danger"] {
      --button-fg: var(--color-danger);
      --button-edge: color-mix(in oklch, var(--color-danger), transparent 55%);
      --button-shade: color-mix(in oklch, var(--color-danger), transparent 80%);

      @media (hover: hover) {
        &:hover:not(:disabled) {
          --button-bg: var(--color-danger-soft);
          --button-edge: var(--color-danger);
        }
      }
    }

    &[data-state="copied"],
    &[data-state="copied"]:hover:not(:disabled) {
      --button-bg: var(--color-success-fill);
      --button-fg: var(--color-on-success);
      --button-edge: transparent;
      --button-shade: color-mix(in oklch, var(--color-success-fill), black 22%);

      animation: pop var(--duration-spring) var(--ease-spring);
    }
  }

  .iconButton {
    inline-size: var(--tap-target);
    padding-inline: 0;
  }

  @keyframes pop {
    from {
      scale: 0.8;
    }
  }

  .visuallyHidden {
    position: absolute;
    inline-size: 1px;
    block-size: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  .input {
    inline-size: 100%;
    min-block-size: var(--tap-target);
    padding-inline: var(--space-4);
    border: 1.5px solid var(--color-border-strong);
    border-radius: var(--radius-md);
    background: var(--color-surface);
    color: var(--color-text);
    font-size: max(16px, var(--text-base));
    box-shadow: inset 0 2px 0 var(--color-surface-sunken);
    transition:
      border-color var(--duration-fast) var(--ease-out),
      box-shadow var(--duration-fast) var(--ease-out);

    &::placeholder {
      color: var(--color-text-faint);
    }

    &:focus-visible {
      outline: none;
      border-color: var(--color-accent);
      box-shadow: 0 0 0 4px color-mix(in oklch, var(--color-accent), transparent 78%);
    }

    &[aria-invalid="true"] {
      border-color: var(--color-danger);

      &:focus-visible {
        box-shadow: 0 0 0 4px color-mix(in oklch, var(--color-danger), transparent 78%);
      }
    }
  }

  .card {
    --color-focus: var(--color-accent);

    border-radius: var(--radius-sheet);
    background: var(--color-surface);
    box-shadow: var(--shadow-sheet);
  }

  .skeleton {
    display: block;
    border-radius: var(--radius-full);
    background: linear-gradient(
        90deg,
        transparent,
        color-mix(in oklch, var(--color-surface), transparent 30%),
        transparent
      )
      var(--color-surface-sunken);
    background-size: 200% 100%;
    animation: shimmer 1.4s linear infinite;
  }

  @keyframes shimmer {
    from {
      background-position: 100% 0;
    }
    to {
      background-position: -100% 0;
    }
  }
}
